/**
 * Sync Queue Manager für Offline-Submission-Synchronisation
 * Verwaltet ausstehende Änderungen, Retry-Logik und Konflikt-Erkennung
 */

export interface PendingSyncItem {
  id: string;
  submissionId: string;
  formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY';
  data: Record<string, any>;
  localTimestamp: number; // Wann wurde lokal geändert
  remoteTimestamp?: number; // Letzte bekannte Remote-Änderung
  retryCount: number;
  lastRetryTime?: number;
  conflicted?: boolean; // Wurde Konflikt erkannt?
  errorMessage?: string;
}

interface SyncQueueState {
  items: PendingSyncItem[];
  lastSyncTime?: number;
  syncInProgress?: boolean;
}

class SyncQueue {
  // Die Schlüssel tragen den alten Repo-Namen und bleiben so: Unter ihnen liegen auf den Geräten
  // der Techniker noch nicht übertragene Berichte — ein neuer Name ließe sie unsichtbar liegen.
  private queueKey = 'cw-portal-sync-queue';
  private lockKey = 'cw-portal-sync-lock';
  private maxRetries = 3;
  private maxRetryDelayMs = 30000; // 30 Sekunden max Backoff
  /** In diesem Tab geöffnete Berichte — siehe hold(). Nur im Speicher, gilt je Tab. */
  private held = new Set<string>();

  /**
   * Aktuellen Queue-Status aus localStorage abrufen
   */
  private getQueueState(): SyncQueueState {
    try {
      const stored = localStorage.getItem(this.queueKey);
      return stored ? JSON.parse(stored) : { items: [] };
    } catch (error) {
      console.error('[SyncQueue] Error reading queue state:', error);
      return { items: [] };
    }
  }

  /**
   * Queue-Status in localStorage speichern
   */
  private saveQueueState(state: SyncQueueState): void {
    try {
      localStorage.setItem(this.queueKey, JSON.stringify(state));
    } catch (error) {
      console.error('[SyncQueue] Error saving queue state:', error);
    }
  }

  /**
   * Submission zur Sync-Queue hinzufügen.
   *
   * `remoteTimestamp` ist immer gesetzt: Nur dann prüft der Server beim Nachsenden, ob der Bericht
   * inzwischen an anderer Stelle weiterbearbeitet wurde (routes/sync.ts vergleicht mit
   * `localTimestamp`). Ohne ihn überschrieb der Abgleich bis v1.354.2 blind — auch die neuere
   * Arbeit von einem zweiten Gerät.
   */
  addPending(submissionId: string, formType: 'ONSITE_SERVICE' | 'ONSITE_DELIVERY', data: Record<string, any>): void {
    const state = this.getQueueState();
    const existingIndex = state.items.findIndex((item) => item.submissionId === submissionId);
    const now = Date.now();

    const item: PendingSyncItem = {
      id: `${submissionId}-${now}`,
      submissionId,
      formType,
      data,
      localTimestamp: now,
      remoteTimestamp: now,
      retryCount: 0,
      conflicted: false,
    };

    if (existingIndex >= 0) {
      // Aktualisiere existierende Einträge - merke remote timestamp
      state.items[existingIndex] = {
        ...item,
        remoteTimestamp: state.items[existingIndex].remoteTimestamp ?? now,
      };
    } else {
      state.items.push(item);
    }

    this.saveQueueState(state);
    console.log('[SyncQueue] Added pending sync:', submissionId);
  }

  /**
   * Alle ausstehenden Syncs abrufen
   */
  getPending(): PendingSyncItem[] {
    const state = this.getQueueState();
    return state.items;
  }

  /** Eintrag zu einem Bericht, falls einer wartet. */
  get(submissionId: string): PendingSyncItem | undefined {
    return this.getQueueState().items.find((item) => item.submissionId === submissionId);
  }

  /**
   * Nur Syncs die bereit sind zum Synchronisieren
   * (nicht zu viel retried, kein aktiver Sync läuft)
   */
  getReadyForSync(): PendingSyncItem[] {
    const state = this.getQueueState();
    const now = Date.now();

    return state.items.filter((item) => {
      // In diesem Tab geöffnet: Die Seite speichert selbst (hold)
      if (this.held.has(item.submissionId)) {
        return false;
      }

      // Skip Konflikte - müssen manuell gelöst werden
      if (item.conflicted) {
        return false;
      }

      // Skip wenn max retries erreicht
      if (item.retryCount >= this.maxRetries) {
        return false;
      }

      // Skip wenn gerade in Cooldown (exponential backoff)
      if (item.lastRetryTime) {
        const backoffMs = Math.min(1000 * Math.pow(2, item.retryCount - 1), this.maxRetryDelayMs);
        if (now - item.lastRetryTime < backoffMs) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Sync-Eintrag als erfolgreich markieren (entfernen)
   */
  markSynced(submissionId: string): void {
    const state = this.getQueueState();
    state.items = state.items.filter((item) => item.submissionId !== submissionId);
    state.lastSyncTime = Date.now();
    this.saveQueueState(state);
    console.log('[SyncQueue] Marked as synced:', submissionId);
  }

  /**
   * Sync-Eintrag als fehlgeschlagen markieren (retry).
   *
   * `countsAsAttempt: false` für Server-Ausfälle (Deploy, Netz): Die sagen nichts über den Bericht
   * und dürfen die drei Versuche nicht verbrauchen — sonst wäre ein Bericht nach einem Deploy von
   * zwei Minuten „endgültig fehlgeschlagen".
   */
  markFailed(submissionId: string, error?: string, { countsAsAttempt = true }: { countsAsAttempt?: boolean } = {}): void {
    const state = this.getQueueState();
    const item = state.items.find((i) => i.submissionId === submissionId);

    if (item) {
      if (countsAsAttempt) item.retryCount++;
      item.lastRetryTime = Date.now();
      item.errorMessage = error;
      this.saveQueueState(state);
      console.log('[SyncQueue] Marked as failed (retry', item.retryCount, '):', submissionId);
    }
  }

  /**
   * Bericht ist in diesem Tab geöffnet: Dann ist die Seite für ihn zuständig — sie hat den neuesten
   * Stand und speichert selbst. Der Hintergrund-Abgleich lässt ihn aus, sonst schöbe er einen
   * älteren Schnappschuss hinterher. Gibt die Freigabe zurück (für den Effekt-Cleanup).
   */
  hold(submissionId: string): () => void {
    this.held.add(submissionId);
    return () => {
      this.held.delete(submissionId);
    };
  }

  isHeld(submissionId: string): boolean {
    return this.held.has(submissionId);
  }

  /**
   * Zähler zurücksetzen, damit ein ausgeschöpfter Eintrag wieder mitläuft.
   *
   * Nach `maxRetries` fehlgeschlagenen Versuchen überspringt `getReadyForSync()`
   * den Eintrag dauerhaft — er bliebe für immer als „ausstehend" stehen, selbst
   * wenn die Ursache längst behoben ist. Manuelles Wiederholen braucht deshalb
   * diesen Reset.
   */
  resetRetries(submissionId: string): void {
    const state = this.getQueueState();
    const item = state.items.find((i) => i.submissionId === submissionId);
    if (!item) return;
    item.retryCount = 0;
    item.lastRetryTime = undefined;
    item.errorMessage = undefined;
    item.conflicted = false;
    this.saveQueueState(state);
  }

  /** Maximale Versuche je Eintrag — für die Anzeige „endgültig fehlgeschlagen". */
  getMaxRetries(): number {
    return this.maxRetries;
  }

  /**
   * Konflikt erkannt - beide Versionen existieren
   * Remote timestamp wird gespeichert für spätere Resolution
   */
  markConflicted(
    submissionId: string,
    remoteTimestamp: number,
    conflictReason?: string
  ): void {
    const state = this.getQueueState();
    const item = state.items.find((i) => i.submissionId === submissionId);

    if (item) {
      item.conflicted = true;
      item.remoteTimestamp = remoteTimestamp;
      item.errorMessage = conflictReason || 'Conflict detected - manual resolution needed';
      this.saveQueueState(state);
      console.log('[SyncQueue] Marked as conflicted:', submissionId);
    }
  }

  /**
   * Konflikt manuell auflösen
   */
  resolveConflict(submissionId: string, resolvedData: Record<string, any>): void {
    const state = this.getQueueState();
    const item = state.items.find((i) => i.submissionId === submissionId);

    if (item) {
      item.data = resolvedData;
      item.conflicted = false;
      item.retryCount = 0; // Reset retries nach Resolution
      item.lastRetryTime = undefined;
      item.localTimestamp = Date.now();
      this.saveQueueState(state);
      console.log('[SyncQueue] Conflict resolved:', submissionId);
    }
  }

  /**
   * Eintrag komplett entfernen (z.B. gelöschte Submission)
   */
  remove(submissionId: string): void {
    const state = this.getQueueState();
    state.items = state.items.filter((item) => item.submissionId !== submissionId);
    this.saveQueueState(state);
    console.log('[SyncQueue] Removed pending sync:', submissionId);
  }

  /**
   * Sync-Lock setzen (verhindert parallele Syncs)
   */
  acquireLock(): boolean {
    const now = Date.now();
    const existing = localStorage.getItem(this.lockKey);

    if (existing) {
      const lockTime = parseInt(existing, 10);
      // Lock verfällt nach 30 Sekunden (Sicherheit für abgebrochene Syncs)
      if (now - lockTime < 30000) {
        return false;
      }
    }

    localStorage.setItem(this.lockKey, now.toString());
    return true;
  }

  /**
   * Sync-Lock freigeben
   */
  releaseLock(): void {
    localStorage.removeItem(this.lockKey);
  }

  /**
   * True, wenn aktuell ein Sync läuft: Lock gehalten und nicht abgelaufen
   * (gleiche 30s-Grenze wie acquireLock). Quelle für den "Syncing…"-UI-Status.
   */
  isSyncInProgress(): boolean {
    const existing = localStorage.getItem(this.lockKey);
    if (!existing) return false;
    const lockTime = parseInt(existing, 10);
    if (Number.isNaN(lockTime)) return false;
    return Date.now() - lockTime < 30000;
  }

  /**
   * Statistiken über ausstehende Syncs
   */
  getStats() {
    const state = this.getQueueState();
    const ready = this.getReadyForSync();

    return {
      totalPending: state.items.length,
      readyForSync: ready.length,
      conflicted: state.items.filter((i) => i.conflicted).length,
      failedAfterRetry: state.items.filter((i) => i.retryCount >= this.maxRetries).length,
      lastSyncTime: state.lastSyncTime,
      syncing: this.isSyncInProgress(),
    };
  }

  /**
   * Gesamte Queue löschen (nur in Debug/Entwicklung)
   */
  clear(): void {
    localStorage.removeItem(this.queueKey);
    localStorage.removeItem(this.lockKey);
    console.log('[SyncQueue] Queue cleared');
  }
}

// Singleton instance
let queueInstance: SyncQueue | null = null;

export function getSyncQueue(): SyncQueue {
  if (!queueInstance) {
    queueInstance = new SyncQueue();
  }
  return queueInstance;
}

export type { SyncQueueState };
